import type { VariantProps } from "class-variance-authority";
import type React from "react";

import { cn } from "@ui/lib/utils";
import { cva } from "class-variance-authority";

const aspectRatioVariants = cva("relative overflow-hidden", {
  variants: {
    ratio: {
      "1x1": "aspect-1x1",
      "3x2": "aspect-3x2",
      "3x4": "aspect-[3/4]",
      "4x3": "aspect-4x3",
      "16x9": "aspect-16x9",
      "21x9": "aspect-21x9",
      "2x1": "aspect-2x1",
      responsive: "aspect-1x1 lg:aspect-16x9",
      responsiveMD: "aspect-1x1 md:aspect-16x9",
    },
    border: {
      true: "border",
      false: "",
    },
    rounded: {
      true: "rounded-xl",
      lg: "rounded-lg",
      md: "rounded-md",
      xl: "rounded-xl",
      xxl: "rounded-xxl",
      false: "",
    },
    centered: {
      true: "flex justify-center items-center",
      false: "",
    },
  },
  defaultVariants: {
    rounded: true,
    centered: true,
    border: false,
  },
});

type AspectRatioVariants = VariantProps<typeof aspectRatioVariants>;
export type AspectRatioKey = AspectRatioVariants["ratio"];
export type AspectRatioRounded = AspectRatioVariants["rounded"];
export interface AspectRatioProps extends React.HTMLAttributes<HTMLDivElement>, AspectRatioVariants {
  height?: null | number;
  ref?: React.Ref<HTMLDivElement>;
  width?: null | number;
}

export const getAspectRatio = (width?: number, height?: number) => {
  if (!width || !height) return null;
  const ratio = width / height;

  if (ratio === 1) return "1x1";
  if (Math.abs(ratio - 3 / 4) < 0.01) return "3x4";
  if (Math.abs(ratio - 3 / 2) < 0.01) return "3x2";
  if (Math.abs(ratio - 4 / 3) < 0.01) return "4x3";
  if (Math.abs(ratio - 16 / 9) < 0.01) return "16x9";
  if (Math.abs(ratio - 21 / 9) < 0.01) return "21x9";
  if (Math.abs(ratio - 2 / 1) < 0.01) return "2x1";
  return "unknown";
};

const AspectRatio = ({
  className,
  ratio = "16x9",
  rounded,
  border,
  centered,
  width,
  height,
  ref,
  ...props
}: AspectRatioProps) => {
  const hasCustomRatio = typeof width === "number" && typeof height === "number";
  return (
    <div
      className={cn(
        aspectRatioVariants({ ratio: hasCustomRatio ? null : ratio, border, rounded, centered, className }),
      )}
      ref={ref}
      style={hasCustomRatio ? { aspectRatio: (width || 16) / (height || 9) } : undefined}
      {...props}
    />
  );
};

AspectRatio.displayName = "AspectRatio";

export { AspectRatio, aspectRatioVariants };
